import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import AOS from 'aos'
import Text from '../../../../elements/Typo/Text'
import InsightCard from './_components/InsightCard'
import { useDeepDiveApi } from '../../../../store/api/hooks/deepDiveApiHook'
import { useParams } from 'react-router-dom'
import { ErrorResponse } from '../../../../store/api/apiTypes'
import { convertToKFormat, formatATE } from '../../../../_common/functions'
import Loader from '../../../../components/loader'
import { ADVANCE_CONFIG_OPTION } from '../../../../_common/constant'

interface InsightDataI {
    [key: string]: { title: string; value: string }[]
}
interface DroDownOptionI {
    label: string
    value: string
}
interface Props {
    cardDetails?: any
    selectedGroup: string | null
    // selectedKPI: any
    kpi_literal: string | null
    kpi_value: string | null
    kpi_unit: string | null
    selectedDateRange: any[]
    selectedOptionConfig?: string | null
    toDownloadAsReport?: boolean
    reportError?: (x: boolean) => void
}

const formatPercentValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
        return '-'
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim()

        if (trimmedValue === '') {
            return '-'
        }

        const parsedValue = Number(trimmedValue)
        if (!Number.isFinite(parsedValue)) {
            return trimmedValue
        }

        return `${parsedValue.toFixed(2)}%`
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value.toFixed(2)}%`
    }

    return String(value)
}

const formatTotalEffectValue = (value: unknown, fallbackValue: string): { value: string; showUnit: boolean } => {
    if (value === null || value === undefined || value === '') {
        return { value: fallbackValue, showUnit: false }
    }

    if (typeof value === 'string') {
        const trimmedValue = value.trim()

        if (trimmedValue === '') {
            return { value: fallbackValue, showUnit: false }
        }

        const parsedValue = Number(trimmedValue)
        if (!Number.isFinite(parsedValue)) {
            return { value: trimmedValue, showUnit: false }
        }

        value = parsedValue
    }

    const numericValue = typeof value === 'number' ? value : Number(value)

    if (!Number.isFinite(numericValue)) {
        return { value: fallbackValue, showUnit: false }
    }

    return {
        value: numericValue >= 0 ? `${convertToKFormat(numericValue)}` : `-${convertToKFormat(numericValue)}`,
        showUnit: true,
    }
}

const InsightOverview: React.FC<Props> = ({ cardDetails, selectedGroup, kpi_literal, kpi_value, kpi_unit, selectedDateRange, selectedOptionConfig, toDownloadAsReport = false, reportError }) => {
    const { t } = useTranslation()
    const [insightData, setInsightData] = useState<InsightDataI>({})
    const [insightDataLoading, setInsightDataLoading] = useState<boolean>(false)
    const selectedKPIValue = kpi_value ?? null

    const params = useParams()

    const { getInsightOverview } = useDeepDiveApi()

    const getDefaultKPIUnit = () => {
        return kpi_unit || ''
    }

    useEffect(() => {
        if (selectedGroup) {
            setInsightDataLoading(true)
            const postData: any = {
                literal: params.exptLiteral,
                group_name: selectedGroup,
                methodology: selectedOptionConfig,
                kpiname: selectedKPIValue,
            }

            if (selectedDateRange.length == 2) {
                postData.measurement_start_date = selectedDateRange[0]
                postData.measurement_end_date = selectedDateRange[1]
                postData.is_slice_and_dice = true
            } else {
                postData.measurement_start_date = null
                postData.measurement_end_date = null
                postData.is_slice_and_dice = false
            }
            getInsightOverview(
                postData,
                (data: any) => {
                    const defaultKPI = getDefaultKPIUnit()
                    const result = Object.keys(data?.data).reduce((a: any, key: string) => {
                        const unit = kpi_unit !== undefined && kpi_unit !== null && kpi_unit !== '' ? kpi_unit : defaultKPI
                        const notApplicableValue = t('NA')

                        const selectedDidMethodoly: boolean = data?.data[key]['methodology'] && data?.data[key]['methodology'].toLowerCase() === ADVANCE_CONFIG_OPTION.DID.value

                        let total = notApplicableValue
                        let totalHasUnit = false
                        if (cardDetails?.kpi_impact_value != null) {
                            const formattedTotal = formatTotalEffectValue(data?.data[key]?.Total_Effect, notApplicableValue)
                            total = formattedTotal.value
                            totalHasUnit = formattedTotal.showUnit
                        }

                        a = {
                            ...a,
                            [key]: [
                                { title: t('Percentage Lift'), value: formatPercentValue(data?.data[key]?.Percentage_lift) },
                                { title: t('Average KPI Lift(ATE)'), value: formatATE(data?.data[key]?.Average_KPI_Lift) },
                                { title: t('Statistical Significance'), value: formatPercentValue(data?.data[key]?.Statistical_Significance), highlight: true, txtValueColor: '#2EA87C' },
                                { title: t('Lift Lower Bound'), value: data?.data[key].Lift_Lower_Bond },
                                { title: t('Lift Upper Bound'), value: data?.data[key].Lift_Upper_Bond },
                                {
                                    title: selectedDidMethodoly ? t('Average Effect') : t('Total Effect'),
                                    value: total,
                                    cardColor: '#F5E003',
                                    unit: totalHasUnit ? unit : '',
                                },
                            ],
                        }
                        return a
                    }, {})
                    setInsightData(result)
                    setInsightDataLoading(false)
                },
                (error: ErrorResponse<[]>) => {
                    console.log(error)
                    if (reportError) {
                        reportError(true)
                    }
                },
            )
        }
    }, [selectedGroup, selectedOptionConfig, selectedKPIValue, selectedDateRange])

    useEffect(() => {
        const params: any = {}

        if (toDownloadAsReport) {
            params['disable'] = true // Animation disabled
        } else {
            params['once'] = false // Animation occurs every time you scroll to the element
        }

        AOS.init(params)
        AOS.refresh()
    }, [])

    return (
        <div className="col-span-3 md:col-span-1 bg-base-100 " data-aos="zoom-in" data-aos-delay="100">
            <div>
                <div className="border px-3 py-3 rounded-[0.45rem] shadow-1  mb-2">
                    <div className="flex justify-between items-center mb-3">
                        <Text size="md" className="font-semibold text-sm mb-2">
                            {t('Insight Overview')}
                        </Text>
                    </div>
                    {insightDataLoading ? (
                        <Loader />
                    ) : (
                        <div className="grid grid-cols-6 gap-4">
                            {selectedKPIValue &&
                                insightData[selectedKPIValue]?.map((stat: any, index: any) => (
                                    <InsightCard key={stat.title} title={stat.title} txtValueColor={stat.txtValueColor} value={stat.value} highlight={stat.highlight} cardColor={stat.cardColor} unit={stat.unit || ''} />
                                ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
export default InsightOverview
